import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Fade, Spinner, Table } from 'react-bootstrap';

import ButtonType from '../../components/ButtonType';
import Delayed from '../../components/Delayed';
import { infoToast } from '../../components/Toasty';
import LoadingWrapper from '../../components/LoadingWrapper';
import MyPagination from '../../components/MyPagination';
import ProjectDropdown from '../../components/ProjectDropdown';
import SelectField from '../../components/SelectField';
import TextField from '../../components/TextField';

import ProjectGoalService from '../../services/ProjectGoalService';

import { userCanEditQualityControlGoals } from '../../functions/AccessHelpers';
import IEmployeeInfo from '../../types/Employee/EmployeeInfo';
import ProjectGoalDetail from '../../types/ProjectGoal/ProjectGoalDetail';
import Project from '../../types/Dashboard/Project';

// Save status constants
const SAVESUCCESS = "SaveSuccess";
const SAVEERROR = "SaveError";

type Props = {
    userData: IEmployeeInfo;
    isMocked: boolean;
    projects: Project[];
    cookieName: string;
};

const QualityControlPage = ({ userData, isMocked, projects, cookieName }: Props) => {
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // State
    const [selectedProject, setSelectedProject] = useState<string | undefined>(undefined);
    const [projectGoalsLoading, setProjectGoalsLoading] = useState(false);
    const [projectGoalDetail, setProjectGoalDetail] = useState<ProjectGoalDetail>({
        projectID: "",
        benchmarkList: [],
        benchmarkDeleteList: [],
        qualityControlList: [],
    });
    const [canEditBenchmarkCreationGoals, setCanEditBenchmarkCreationGoals] = useState(false);
    const [scopeSortType, setScopeSortType] = useState("");
    const [qualityPage, setQualityPage] = useState(1);
    const [qualityPageSize, setQualityPageSize] = useState(10);
    const [qualityIndexOfFirst, setQualityIndexOfFirst] = useState(0);
    const [qualityIndexOfLast, setQualityIndexOfLast] = useState(10);
    const [qualityTradeFilter, setQualityTradeFilter] = useState("");
    const [qualityActivityFilter, setQualityActivityFilter] = useState("");
    const [scopeFilter, setScopeFilter] = useState("");
    const [goalFilter, setGoalFilter] = useState("");
    const [appliesToProjectFilter, setAppliesToProjectFilter] = useState("");
    const [filteredQualityControlLength, setFilteredQualityControlLength] = useState(0);

    // Save state
    const [changesMade, setChangesMade] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [saveStatus, setSaveStatus] = useState("");
    const [showSaveStatus, setShowSaveStatus] = useState(false);

    /**
     * Calculates the length of the filtered list for Quality Control
     */
    const calculateFilteredListLength = useCallback(() => {
        const newFilteredQualityControlLength = projectGoalDetail.qualityControlList
            .slice()
            .filter((entry) =>
                entry.trade.toUpperCase().includes(qualityTradeFilter.toUpperCase()) &&
                entry.activity.toUpperCase().includes(qualityActivityFilter.toUpperCase()) &&
                entry.scope.toUpperCase().includes(scopeFilter.toUpperCase()) &&
                entry.goal.toUpperCase().includes(goalFilter.toUpperCase()) &&
                (entry.appliesToProject ? "1" : "0").includes(
                    appliesToProjectFilter === "Yes" ? "1" : appliesToProjectFilter === "No" ? "0" : ""
                )
            )
            .length;

        setFilteredQualityControlLength(newFilteredQualityControlLength);
    }, [projectGoalDetail.qualityControlList, qualityTradeFilter, qualityActivityFilter, scopeFilter, goalFilter, appliesToProjectFilter]);

    /**
     * Gets ProjectGoals data from the ProjectGoalService
     * @param project companyID
     */
    const getProjectGoals = useCallback(async (project: string) => {
        // Get Benchmark and Quality Control data
        try {
            const response = await ProjectGoalService.getProjectGoalDetails(project);
            if (!mountedRef.current) return;
            if (response.data) {
                setProjectGoalDetail(response.data);
                setProjectGoalsLoading(false);
            }
        } catch (e) {
            if (!mountedRef.current) return;
            setProjectGoalsLoading(false);
        }

        // If user can edit, get BenchmarkDetails data for adding/editing Benchmark Goals
        if (canEditBenchmarkCreationGoals) {
            try {
                await ProjectGoalService.getBenchmarkDetails(project);
            } catch (e) {
                // Handle error silently
            }
        }
    }, [canEditBenchmarkCreationGoals]);

    // Check if user can edit Benchmark Creation Goals on mount
    useEffect(() => {
        setCanEditBenchmarkCreationGoals(userCanEditQualityControlGoals(userData));
    }, [userData.adGroups]);

    // Fetch project goals when selectedProject changes
    useEffect(() => {
        if (!userData?.email || !selectedProject) {
            return;
        }

        setProjectGoalsLoading(true);
        getProjectGoals(selectedProject);
    }, [userData?.email, selectedProject, getProjectGoals]);

    // Recalculate filtered list length when filters or data change
    useEffect(() => {
        calculateFilteredListLength();
    }, [calculateFilteredListLength]);

    /**
     * Saves the updates to the database
     * @param data ProjectGoalDetail to save
     */
    const saveUpdates = async (data: ProjectGoalDetail) => {
        // If we are logged in as a different user, do not allow the save actions to happen
        if (isMocked) {
            infoToast('Logged in as different user, saving unavailable.');
            return;
        }

        setSaveLoading(true);
        setChangesMade(false);
        setShowSaveStatus(true);

        try {
            const response = await ProjectGoalService.updateProjectGoalDetails(data, userData.email);
            if (response.status >= 100 && response.status <= 399) { // Successful responses
                setSaveStatus(SAVESUCCESS);
                setSaveLoading(false);

                // Set showSaveStatus back to false after 10 seconds
                setTimeout(() => {
                    setShowSaveStatus(false);
                    setSaveStatus("");
                }, 10000);
            } else {
                setSaveStatus(SAVEERROR);
                setSaveLoading(false);
            }
        } catch (e) {
            setSaveStatus(SAVEERROR);
            setSaveLoading(false);
        }
    };

    /**
     * Handles goal changes in the Goal input number
     * @param formID activity formID
     * @param value goal
     */
    const handleGoalChange = (formID: string, value: string) => {
        const newProjectGoalDetail = { ...projectGoalDetail };
        const index = newProjectGoalDetail.qualityControlList.findIndex(f => f.formID === formID);

        const parsed = parseInt(value);
        const number = isNaN(parsed) ? 0 : parsed;
        if ((number > -1) && (number < 1000000)) {
            newProjectGoalDetail.qualityControlList[index].goal = number.toString();
            setProjectGoalDetail(newProjectGoalDetail);
            setChangesMade(true);
        }
    };

    /**
     * Handles appliesToProject changes in the Applies to Project input checkbox
     * @param formID activity formID
     */
    const handleAppliesToProjectChange = (formID: string) => {
        const newProjectGoalDetail = { ...projectGoalDetail };
        const index = newProjectGoalDetail.qualityControlList.findIndex(f => f.formID === formID);

        newProjectGoalDetail.qualityControlList[index].appliesToProject =
            !newProjectGoalDetail.qualityControlList[index].appliesToProject;

        setProjectGoalDetail(newProjectGoalDetail);
        setChangesMade(true);
    };

    /**
     * Handles page changes in the Pagination for the Quality Control section
     * @param newPage page number
     */
    const handleQualityPageChange = (newPage: number) => {
        const newIndexOfLast = newPage * qualityPageSize;
        const newIndexOfFirst = newIndexOfLast - qualityPageSize;

        setQualityPage(newPage);
        setQualityIndexOfLast(newIndexOfLast);
        setQualityIndexOfFirst(newIndexOfFirst);
    };

    /**
     * Handles page_size changes in the Page Size SelectField for the Quality Control section
     * @param event page size
     */
    const handleQualityPageSizeChange = (event: any) => {
        const pageSize = parseInt(event.target.value);
        const newLastPage = Math.ceil(filteredQualityControlLength / pageSize);

        let currentPage = qualityPage;
        if (newLastPage < currentPage) {
            currentPage = newLastPage;
        }

        const newIndexOfLast = currentPage * pageSize;
        const newIndexOfFirst = newIndexOfLast - pageSize;

        setQualityPageSize(pageSize);
        setQualityPage(currentPage);
        setQualityIndexOfLast(newIndexOfLast);
        setQualityIndexOfFirst(newIndexOfFirst);
    };

    /**
     * Handles resetting the filters for the Quality Control section
     */
    const handleQualityResetFilters = () => {
        setQualityPage(1);
        setQualityTradeFilter("");
        setQualityActivityFilter("");
        setScopeFilter("");
        setGoalFilter("");
        setAppliesToProjectFilter("");
        setScopeSortType("");
    };

    /**
     * Updates the Scope sorting type from a-z to z-a
     */
    const updateScopeColumnSort = () => {
        let newSort;
        if (scopeSortType === "az") {
            newSort = "za";
        } else if (scopeSortType === "za") {
            newSort = "az";
        } else {
            newSort = "az";
        }
        setScopeSortType(newSort);
    };

    /**
     * Filters the Trade column for Quality Control by input
     */
    const filterQualityTradeColumn = (event: any) => {
        setQualityTradeFilter(event.target.value);
    };

    /**
     * Filters the Activity column for Quality Control by input
     */
    const filterQualityActivityColumn = (event: any) => {
        setQualityActivityFilter(event.target.value);
    };

    /**
     * Filters the Scope column for Quality Control by input
     */
    const filterScopeColumn = (event: any) => {
        setScopeFilter(event.target.value);
    };

    /**
     * Filters the Goal column for Quality Control by input
     */
    const filterGoalColumn = (value: string) => {
        const parsed = parseInt(value);
        const number = isNaN(parsed) ? "" : parsed.toString();
        if ((number === "") || (number.length < 7)) {
            setGoalFilter(number);
        }
    };

    /**
     * Filters the Applies to Project column for Quality Control by input
     */
    const filterAppliesToProjectColumn = (event: any) => {
        setAppliesToProjectFilter(event.target.value);
    };

    /**
     * Sorts alphabetically between two string entries on scope_sort_type
     */
    const sortAlphabetically = (a: string, b: string, reverse: boolean = false) => {
        const sort_modifier = reverse ? -1 : 1;
        const value = (a < b) ? -1 : (b < a) ? 1 : 0;
        return value * sort_modifier;
    };

    // Render Quality Control Items
    const QualityControlItems = projectGoalsLoading ? (
        <tr>
            <td colSpan={100}>
                <Delayed waitBeforeShow={750}>
                    <div className="DivPadded DivCenter">
                        <Spinner animation="border" role="status" variant="primary" />
                    </div>
                </Delayed>
            </td>
        </tr>
    ) : projectGoalDetail.qualityControlList.length > 0 ? (
        projectGoalDetail.qualityControlList
            .sort((a, b) => {
                if (scopeSortType) {
                    return sortAlphabetically(a.scope, b.scope, scopeSortType === "za");
                } else {
                    return sortAlphabetically(a.trade, b.trade);
                }
            })
            .filter((entry) =>
                entry.trade.toUpperCase().includes(qualityTradeFilter.toUpperCase()) &&
                entry.activity.toUpperCase().includes(qualityActivityFilter.toUpperCase()) &&
                entry.scope.toUpperCase().includes(scopeFilter.toUpperCase()) &&
                entry.goal.toUpperCase().includes(goalFilter.toUpperCase()) &&
                (entry.appliesToProject ? "1" : "0").includes(
                    appliesToProjectFilter === "Yes" ? "1" : appliesToProjectFilter === "No" ? "0" : ""
                )
            )
            .slice(qualityIndexOfFirst, qualityIndexOfLast)
            .map((entry, i) => (
                <tr key={`${entry.trade}${entry.activity}${entry.scope}${i}`}>
                    <td>{entry.trade}</td>
                    <td>{entry.activity}</td>
                    <td>{entry.scope}</td>
                    <td>
                        {canEditBenchmarkCreationGoals ? (
                            <input
                                className="Value form-control input-sm"
                                style={{ width: 95, height: 26 }}
                                value={entry.goal}
                                onChange={(e) => handleGoalChange(entry.formID, e.target.value)}
                            />
                        ) : (
                            entry.goal
                        )}
                    </td>
                    <td>
                        <input
                            type="checkbox"
                            checked={entry.appliesToProject}
                            disabled={!canEditBenchmarkCreationGoals}
                            onChange={() => handleAppliesToProjectChange(entry.formID)}
                        />
                    </td>
                </tr>
            ))
    ) : (
        <tr>
            <td colSpan={100}>
                <b>No Entries Found</b>
            </td>
        </tr>
    );

    return (
        <div className='w-75'>
            <LoadingWrapper
                showLoading={!userData?.email}
                loadingMessage="Getting user data..."
                windSpinner
                windSpinnerScale={3}
            >
                <div>
                    <ProjectDropdown
                      projects={projects}
                      cookieName={cookieName}
                      onProjectChange={(project) => {
                        setSelectedProject(project)
                      }}
                    />
                </div>

                {selectedProject ? (
                    <div>
                        <div id="RightAlignedFloatDiv">
                            {canEditBenchmarkCreationGoals ?
                                changesMade ?
                                    <ButtonType
                                        dataName="save"
                                        color="blue"
                                        buttonText="Save"
                                        type="submit"
                                        eventHandler={() => saveUpdates(projectGoalDetail)}
                                    />
                                : saveLoading ?
                                    <div className="DivPadded DivCenter"><Spinner animation="border" role="status" variant="primary" /></div>
                                : saveStatus === SAVESUCCESS ?
                                    <div className="DivPadded DivCenter">
                                        <Fade in={showSaveStatus}>
                                            <b>Save Successful ✔</b>
                                        </Fade>
                                    </div>
                                : saveStatus === SAVEERROR ?
                                    <div className="DivPadded DivCenter">
                                        <b>An Error Occurred ⚠️</b><p><small><i>Please Refresh and Try Again</i></small></p>
                                    </div>
                                : null
                            : null}
                        </div>
                        <h1 className="header mt-5 mb-4 text-center">Quality Control</h1>
                        <div className="d-flex justify-content-start align-items-center">
                            <b><label htmlFor="pageSize">Page Size:</label></b>
                            <div className="DivPadded">
                                <SelectField
                                    dataName="pageSize"
                                    title=""
                                    options={[{ value: "10" }, { value: "25" }, { value: "50" }]}
                                    value={qualityPageSize.toString()}
                                    onchange={handleQualityPageSizeChange}
                                />
                            </div>
                            {qualityTradeFilter || qualityActivityFilter || scopeFilter || goalFilter || appliesToProjectFilter || scopeSortType ? (
                                <ButtonType
                                    dataName="filterReset"
                                    buttonText="Reset Filters"
                                    eventHandler={() => handleQualityResetFilters()}
                                />
                            ) : null}
                        </div>
                        <div className="d-flex flex-wrap align-items-center">
                            <div className="p2">
                                <MyPagination
                                    currentPage={qualityPage}
                                    pages={Math.ceil(filteredQualityControlLength / qualityPageSize)}
                                    onPageChange={(newPage) => handleQualityPageChange(newPage)}
                                />
                            </div>
                            <div className="flex-grow-1 flex-wrap DivRight">
                                <p><i>Note: This data is refreshed nightly</i></p>
                            </div>
                        </div>
                        <Table>
                            <thead>
                                <tr>
                                    <th style={{ width: 125 }}>Trade</th>
                                    <th style={{ width: 180 }}>Activity</th>
                                    <th className="ColumnSort" onClick={updateScopeColumnSort} style={{ width: 170 }}>
                                        Scope {scopeSortType === "" ? " ⮃" : scopeSortType === "az" ? " ⭡" : " ⭣"}
                                    </th>
                                    <th style={{ width: 50 }}>Goal</th>
                                    <th style={{ width: 75 }}>Applies to Project</th>
                                </tr>
                                <tr key="qualityControlFilters">
                                    <td>
                                        <TextField
                                            dataName="qualityTradeFilter"
                                            title=""
                                            value={qualityTradeFilter}
                                            onchange={filterQualityTradeColumn}
                                            placeholder="filter trade"
                                        />
                                    </td>
                                    <td>
                                        <TextField
                                            dataName="qualityActivityFilter"
                                            title=""
                                            value={qualityActivityFilter}
                                            onchange={filterQualityActivityColumn}
                                            placeholder="filter activity"
                                        />
                                    </td>
                                    <td>
                                        <TextField
                                            dataName="scopeFilter"
                                            title=""
                                            value={scopeFilter}
                                            onchange={filterScopeColumn}
                                            placeholder="filter scope"
                                        />
                                    </td>
                                    <td>
                                        <TextField
                                            dataName="goalFilter"
                                            title=""
                                            value={goalFilter}
                                            onchange={(e) => filterGoalColumn(e.target.value)}
                                            placeholder="filter goal"
                                        />
                                    </td>
                                    <td>
                                        <SelectField
                                            dataName="appliesToProjectFilter"
                                            title=""
                                            value={appliesToProjectFilter}
                                            onchange={filterAppliesToProjectColumn}
                                            options={[{ value: "Yes" }, { value: "No" }]}
                                            allowSelectOption={true}
                                        />
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                {QualityControlItems}
                            </tbody>
                        </Table>
                        <br />
                        <br />
                        <br />
                        <br />
                    </div>
                ) : (
                    <h1 className="header mt-5 mb-4 text-center">Project Goals</h1>
                )}
            </LoadingWrapper>
        </div>
    );
};

export default QualityControlPage;
